import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby';
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
  useScrollTrigger,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Close from '@mui/icons-material/Close';
import Logo from './Logo';
import CalButton from './CalButton';
import { services } from '../data/services';

function ElevationScroll({ children }) {
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 8 });
  return React.cloneElement(children, {
    elevation: trigger ? 1 : 0,
    sx: {
      ...children.props.sx,
      background: trigger
        ? 'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(239,246,255,0.86))'
        : 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(239,246,255,0.72))',
      backdropFilter: 'blur(12px)',
      boxShadow: trigger ? '0 1px 12px rgba(15,23,42,0.06)' : 'none',
      borderBottom: '1px solid rgba(37,99,235,0.10)',
    },
  });
}

function useHideOnScroll() {
  const [hidden, setHidden] = useState(false);
  const lastY = React.useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 80) {
        setHidden(false);
      } else if (y > lastY.current + 4) {
        setHidden(true);
      } else if (y < lastY.current - 4) {
        setHidden(false);
      }
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return hidden;
}

export default function Navbar() {
  const [drawer, setDrawer] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const hidden = useHideOnScroll();

  const openServices = (e) => setAnchor(e.currentTarget);
  const closeServices = () => setAnchor(null);

  return (
    <ElevationScroll>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{
          transform: hidden ? 'translateY(-100%)' : 'translateY(0)',
          transition: 'transform 0.3s ease',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Toolbar disableGutters sx={{ minHeight: 68 }}>
            <Logo width={70} height={70} fontSize={25} />
            <Box sx={{ flexGrow: 1 }} />
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              <Button
                component={Link}
                to="/"
                color="inherit"
                sx={{ color: '#0F172A', fontWeight: 600 }}
              >
                Home
              </Button>
              <Button
                color="inherit"
                onClick={openServices}
                endIcon={<ExpandMore />}
                sx={{ color: '#0F172A', fontWeight: 600 }}
              >
                Services
              </Button>
              <Button
                component={Link}
                to="/about"
                color="inherit"
                sx={{ color: '#0F172A', fontWeight: 600 }}
              >
                About
              </Button>
              <Button
                component={Link}
                to="/contact"
                color="inherit"
                sx={{ color: '#0F172A', fontWeight: 600 }}
              >
                Contact
              </Button>
            </Stack>
            <Box sx={{ ml: 1.5, display: { xs: 'none', md: 'block' } }}>
              <CalButton type="demo" size="medium">
                Book a consultation
              </CalButton>
            </Box>
            <IconButton
              sx={{ ml: 1, display: { md: 'none' } }}
              onClick={() => setDrawer(true)}
              aria-label="Open menu"
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>

        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={closeServices}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 3,
              minWidth: 320,
              p: 1,
              backgroundColor: 'rgba(255,255,255,0.72)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
              border: '1px solid rgba(255,255,255,0.65)',
              boxShadow: '0 16px 48px rgba(15,23,42,0.14), inset 0 1px 0 rgba(255,255,255,0.6)',
            },
          }}
        >
          {services.map((s) => (
            <MenuItem
              key={s.slug}
              component={Link}
              to={`/services/${s.slug}/`}
              onClick={closeServices}
              sx={{ borderRadius: 2 }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{s.name}</Typography>
                <Typography sx={{ color: '#64748B', fontSize: 12.5 }}>{s.tagline}</Typography>
              </Box>
            </MenuItem>
          ))}
          <Divider sx={{ my: 1 }} />
          <MenuItem
            component={Link}
            to="/services/"
            onClick={closeServices}
            sx={{ borderRadius: 2 }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#2563EB' }}>
              View all services
            </Typography>
          </MenuItem>
        </Menu>

        <Drawer
          anchor="right"
          open={drawer}
          onClose={() => setDrawer(false)}
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(255,255,255,0.78)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
              borderLeft: '1px solid rgba(255,255,255,0.6)',
              boxShadow: '-12px 0 48px rgba(15,23,42,0.12)',
            },
          }}
        >
          <Box sx={{ width: 300, p: 2 }}>
            <Stack direction="row" justifyContent="flex-end">
              <IconButton onClick={() => setDrawer(false)} aria-label="Close menu">
                <Close />
              </IconButton>
            </Stack>
            <List>
              <ListItemButton component={Link} to="/" onClick={() => setDrawer(false)}>
                <ListItemText primary="Home" />
              </ListItemButton>
              <ListItemButton onClick={() => setDrawer(false)}>
                <ListItemText
                  primary="Services"
                  secondary="7 practices: AI, consulting, engineering & QA"
                  onClick={() => setDrawer(false)}
                />
              </ListItemButton>
              <Box sx={{ pl: 3 }}>
                {services.map((s) => (
                  <ListItem key={s.slug} disablePadding sx={{ py: 0.25 }}>
                    <ListItemButton
                      component={Link}
                      to={`/services/${s.slug}/`}
                      onClick={() => setDrawer(false)}
                    >
                      <ListItemText primary={s.name} primaryTypographyProps={{ fontSize: 14 }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Box>
              <ListItemButton component={Link} to="/about" onClick={() => setDrawer(false)}>
                <ListItemText primary="About" />
              </ListItemButton>
              <ListItemButton component={Link} to="/contact" onClick={() => setDrawer(false)}>
                <ListItemText primary="Contact" />
              </ListItemButton>
            </List>
            <Box sx={{ px: 2, mt: 2 }}>
              <CalButton type="demo" fullWidth onClick={() => setDrawer(false)}>
                Book a consultation
              </CalButton>
            </Box>
          </Box>
        </Drawer>
      </AppBar>
    </ElevationScroll>
  );
}
